/** @jsx jsx */
import { jsx } from '@emotion/react';

import type * as React from 'react';
import { useMemo } from 'react';

import { breakpoints, useMediaQuery } from '@coursera/cds-core';

import type { KnownDomainSlugs } from 'bundles/browse-common/types/domain';
import { toSearchTopic } from 'bundles/browse-common/utils/toSearchTopic';
import {
  DEFAULT_SORTED_VALUE,
  NUMBER_OF_RESULTS_FOR_DOMAIN_MOBILE_SEARCH,
  NUMBER_OF_RESULTS_PER_PAGE,
  SEARCH_MAIN_INDEX_ID,
} from 'bundles/search-common/constants';
import { DEFAULT_SEARCH_FILTERS, IS_PART_OF_COURSERA_PLUS } from 'bundles/search-common/constants/filter';
import SearchProvider from 'bundles/search-common/providers/SearchProvider';
import type { SearchConfig } from 'bundles/search-common/providers/searchTypes';

type Props = {
  domainSlug?: KnownDomainSlugs;
  children: React.ReactNode;
};

export const getMobileSearchConfigs = (showCourseraPlusFilter: boolean, domainSlug?: string): SearchConfig[] => {
  const filterAttributes = [
    ...DEFAULT_SEARCH_FILTERS.filter((filter) => filter !== 'topic'),
    ...(showCourseraPlusFilter ? [IS_PART_OF_COURSERA_PLUS] : []),
  ];
  const topic = domainSlug ? toSearchTopic(domainSlug) : undefined;

  return [
    {
      id: SEARCH_MAIN_INDEX_ID,
      entityType: 'PRODUCTS',
      limit: NUMBER_OF_RESULTS_FOR_DOMAIN_MOBILE_SEARCH,
      facets: filterAttributes,
      facetFilters: topic ? [[`topic:${topic}`]] : [],
      syncQueryWithUrl: false,
      syncFiltersWithUrl: false,
      sortBy: DEFAULT_SORTED_VALUE,
    },
  ];
};

export const getDesktopSearchConfigs = (showCourseraPlusFilter: boolean, domainSlug?: string): SearchConfig[] => {
  const filterAttributes = [
    ...DEFAULT_SEARCH_FILTERS.filter((filter) => filter !== 'topic'),
    ...(showCourseraPlusFilter ? [IS_PART_OF_COURSERA_PLUS] : []),
  ];
  const topic = domainSlug ? toSearchTopic(domainSlug) : undefined;

  return [
    {
      id: SEARCH_MAIN_INDEX_ID,
      entityType: 'PRODUCTS',
      limit: NUMBER_OF_RESULTS_PER_PAGE,
      facets: filterAttributes,
      facetFilters: topic ? [[`topic:${topic}`]] : [],
      syncQueryWithUrl: false,
      syncFiltersWithUrl: false,
      sortBy: DEFAULT_SORTED_VALUE,
    },
  ];
};

const DomainSearchProvider = (props: Props) => {
  const { domainSlug, children } = props;
  const isMobile = useMediaQuery(breakpoints.down('xs'), { noSsr: true });

  const searchConfigs = useMemo(
    () => (isMobile ? getMobileSearchConfigs(false, domainSlug) : getDesktopSearchConfigs(false, domainSlug)),
    [domainSlug, isMobile]
  );
  return (
    <SearchProvider key={isMobile ? 'mobile' : 'desktop'} searchConfigs={searchConfigs}>
      {children}
    </SearchProvider>
  );
};

export default DomainSearchProvider;
